/**
 * PURE — the Categories step: match each category in the file to one real
 * category, exactly, and write its name and id back separately.
 *
 * What this replaces, and why: the step used to offer a flat list mixing
 * category names ("Support & Risk Signals") with category ids ("support"),
 * match with a "contains the word" guess, and overwrite the row's category with
 * whichever string won. "Financial Contract Performance" became `financial`,
 * "Account Health" stayed a name, and the file's own Category ID column was
 * dropped before any of it.
 *
 * The rules now, and nothing looser:
 *   1. The file's Category ID equals a category's id → that category.
 *   2. The category text equals a category's id, name, long name, or one of
 *      its known names (e.g. the names customer files use) → that category,
 *      but only when exactly one category claims it.
 *   3. If 1 and 2 disagree, the file contradicts itself and a person decides.
 *   4. Otherwise a person decides. A guess here files metrics under the wrong
 *      category, and nothing downstream says so.
 *
 * The row's `category` gets the catalog's name and a separate column gets the
 * id. The catalog is the authority: a row keeps the file's id only when its
 * category could not be resolved, and QC then flags any metric whose catalog
 * category disagrees.
 */
import {
  effectiveTarget,
  topMatches,
  type Candidate,
  type MatchProposal,
} from "./entity-match";
import type { CategoryOption } from "../data/api";

/** Hidden upload column carrying the resolved category id (→ `category_id`). */
export const CATEGORY_ID_COLUMN = "__resolved_category_id";

const key = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/** Every name that identifies `o`, lower-cased. */
function namesOf(o: CategoryOption): string[] {
  return [o.key, o.label, o.long_label ?? "", ...(o.aliases ?? [])]
    .map(key)
    .filter(Boolean);
}

/** One dropdown entry per category: "Support & Risk Signals (support)". */
export function categoryCandidates(options: CategoryOption[]): Candidate[] {
  return options.map(o => ({
    id: o.key,
    name: `${o.long_label || o.label} (${o.key})`,
  }));
}

/** The catalog's name for a category, as written into the row. */
export function categoryLabel(o: CategoryOption): string {
  return o.label || o.key;
}

/** Distinct Category ID values the file pairs with each category text. */
export function fileIdsByCategory(
  rows: Record<string, string>[],
  categoryCol: string | null,
  categoryIdCol: string | null
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!categoryCol || !categoryIdCol) return out;
  rows.forEach(r => {
    const text = (r[categoryCol] ?? "").trim();
    const id = (r[categoryIdCol] ?? "").trim();
    if (!text || !id) return;
    const list = out[text] ?? (out[text] = []);
    if (list.indexOf(id) === -1) list.push(id);
  });
  return out;
}

/** Propose the category for one value from the file. See the module comment. */
export function proposeCategory(
  source: string,
  fileIds: string[],
  options: CategoryOption[],
  rowCount: number
): MatchProposal {
  const candidates = categoryCandidates(options);
  // In the order given: what the name says first, then what the id says.
  const suggestionsFor = (keys: string[]) =>
    keys
      .map(k => candidates.filter(c => c.id === k)[0])
      .filter(Boolean)
      .map(c => ({ ...c, score: 100 }));

  // What the file's id says, when the file is consistent about it.
  const byId =
    fileIds.length === 1
      ? options.filter(o => key(o.key) === key(fileIds[0]))
      : [];
  const idPick = byId.length === 1 ? byId[0].key : null;

  // What the name says, when exactly one category claims it.
  const wanted = key(source);
  const byName = options.filter(o => namesOf(o).indexOf(wanted) !== -1);
  const namePick = byName.length === 1 ? byName[0].key : null;

  // The file contradicts itself — e.g. "Customer Experience & Delight" rows
  // carrying Category ID "health". Either choice could be the mistake, so a
  // person decides, seeing both.
  if (idPick && namePick && idPick !== namePick) {
    const [byNameCandidate, byIdCandidate] = suggestionsFor([namePick, idPick]);
    return {
      source,
      rowCount,
      suggestions: suggestionsFor([namePick, idPick]),
      detail: [
        `Name says: ${byNameCandidate?.name ?? namePick}`,
        `Category ID “${fileIds[0]}” says: ${byIdCandidate?.name ?? idPick}`,
      ],
      status: "ambiguous",
      chosenId: null,
      how: null,
    };
  }

  const base = {
    source,
    rowCount,
    // Advisory only, shown beside the row; never applied without a person.
    suggestions: topMatches(source, candidates),
  };
  if (idPick) {
    return { ...base, status: "resolved", chosenId: idPick, how: "exact" };
  }
  if (namePick) {
    return {
      ...base,
      status: "resolved",
      chosenId: namePick,
      how: "normalized",
    };
  }
  if (byName.length > 1) {
    return { ...base, status: "ambiguous", chosenId: null, how: null };
  }
  return { ...base, status: "unresolved", chosenId: null, how: null };
}

/**
 * Write the step's decisions into the rows.
 *
 * Resolved: `category` ← the catalog's name, CATEGORY_ID_COLUMN ← its id.
 * Unresolved: the row keeps its own text, and its own id if it had one. It
 * still imports — category is descriptive — and QC reports the mismatch.
 */
export function applyCategoryDecisions(
  rows: Record<string, string>[],
  categoryCol: string | null,
  categoryIdCol: string | null,
  proposals: MatchProposal[],
  decisions: Record<string, string>,
  options: CategoryOption[]
): Record<string, string>[] {
  if (!categoryCol) return rows;
  const byKey: Record<string, CategoryOption> = {};
  options.forEach(o => {
    byKey[o.key] = o;
  });
  const target: Record<string, CategoryOption | null> = {};
  proposals.forEach(p => {
    const k = effectiveTarget(p, decisions);
    target[p.source] = k ? (byKey[k] ?? null) : null;
  });

  return rows.map(r => {
    const text = (r[categoryCol] ?? "").trim();
    const chosen = target[text];
    const fileId = categoryIdCol ? (r[categoryIdCol] ?? "").trim() : "";
    if (chosen) {
      return {
        ...r,
        [categoryCol]: categoryLabel(chosen),
        [CATEGORY_ID_COLUMN]: chosen.key,
      };
    }
    return fileId ? { ...r, [CATEGORY_ID_COLUMN]: fileId } : r;
  });
}
